import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  InputBase,
  Paper,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { motion } from "framer-motion";
import {
  Brand_url_top,
  Brand_url_new,
  // Brand_url,
} from "../../api/urls";
import Image from "../../components/Image";
import useFetch from "../../hooks/useFetch";
import NoImage from "../../Assests/placeholder.jpg";
// Styled components (keep the same as before)
const Search = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#f5f5f5",
  width: "100%",
  maxWidth: "300px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: theme.spacing(1),
  color: "rgba(0, 0, 0, 0.54)",
}));

const StyledInputBase = styled(InputBase)(() => ({
  flex: 1,
  input: {
    padding: 0,
    height: "100%",
    fontSize: "0.95rem",
  },
}));

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Brand() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");

  const { data: topBrands, loading: topBrandLoading } = useFetch(
    Brand_url_top + `&search=${searchQuery}`
  );
  const { data: newBrands, loading: newBrandLoading } = useFetch(
    Brand_url_new + `&search=${searchQuery}`
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const navigateToBrandProducts = (catID) => {
    // Navigate to products page with brand filter
    navigate(`/products?brand=${catID}`, {
      state: {
        catID: catID,
      },
    });
  };

  const renderBrandGrid = (list) =>
    list?.map((brand, index) => (
      <motion.div
        key={brand.id || index}
        whileHover={{ scale: 1.05 }}
        style={{
          display: "flex",
          justifyContent: "center",
          cursor: "pointer",
        }}
        onClick={() => navigateToBrandProducts(brand?.brand_name)}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <Image
            src={brand?.brand_image_upload || NoImage}
            alt={brand?.brand_name || `Brand ${index + 1}`}
            loading="lazy"
            style={{ width: "250px", height: "200px", objectFit: "contain" }}
          />
        </Paper>
      </motion.div>
    ));

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "transparent",
          color: "black",
          boxShadow: "none",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              display: { sm: "block" },
              marginLeft: "10px",
            }}
          >
            Brands
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            position="relative"
            marginTop={2}
          >
            {/* <ClickAwayListener onClickAway={handleClickAway}> */}
            <Box position="relative">
              <Search>
                <StyledInputBase
                  placeholder="Search Brands..."
                  inputProps={{ "aria-label": "search" }}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  // onFocus={() =>
                  //   searchResults.length > 0 && setShowResults(true)
                  // }
                />
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
              </Search>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Top Brands */}
      <Box px={2} py={3}>
        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            // mb={5}
            sx={{ textAlign: "left" }}
          >
            <Typography
              sx={{
                flexGrow: 1,
                marginLeft: 2,
                marginTop: 2,
                marginBottom: 2,
                display: { sm: "block" },
                fontSize: "20px",
              }}
            >
              Top Brands
            </Typography>
            {topBrands?.length > 8 && (
              <Typography
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: "#00000080",
                  userSelect: "none",
                  fontSize: "16px",
                }}
                onClick={() => {
                  navigate("/top_brand");
                  // setSeeAllTop(!seeAllTop)
                }}
              >
                See All
                {/* {seeAllTop ? "Show less" : "See all"} */}
              </Typography>
            )}
          </Box>
          {topBrandLoading && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                p: 2,
                width: "100%",
                margin: "auto",
                justifyContent: { xs: "center", md: "start" },
              }}
            >
              <Skeleton height={300} width={300} />
              <Skeleton height={300} width={300} />
            </Box>
          )}
          {!topBrandLoading && (
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              }}
              gap={2}
            >
              {renderBrandGrid(topBrands?.slice(0, 8))}
            </Box>
          )}
        </motion.div>
      </Box>

      {/* New Brands */}
      <Box px={2} py={3}>
        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            // mb={5}
            sx={{ textAlign: "left" }}
          >
            <Typography
              sx={{
                flexGrow: 1,
                marginLeft: 2,
                marginTop: 2,
                marginBottom: 2,
                display: { sm: "block" },
                fontSize: "20px",
              }}
            >
              New Brands
            </Typography>
            {newBrands?.length > 4 && (
              <Typography
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: "#00000080",
                  userSelect: "none",
                  fontSize: "16px",
                }}
                onClick={() => navigate("/new_brand")}
              >
                See All
                {/* {seeAllNew ? "Show less" : "See all"} */}
              </Typography>
            )}
          </Box>
          {newBrandLoading && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                p: 2,
                width: "100%",
                margin: "auto",
                justifyContent: { xs: "center", md: "start" },
              }}
            >
              <Skeleton height={300} width={300} />
              <Skeleton height={300} width={300} />
            </Box>
          )}
          {!newBrandLoading && (
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              }}
              gap={2}
            >
              {renderBrandGrid(newBrands?.slice(0, 8))}
            </Box>
          )}
        </motion.div>
      </Box>
    </>
  );
}
